// Phase 1A · Step 25 · Phase 1 — mode-architecture constants.
//
// This file is the type-safe anchor for STRIKZA's locked five-mode
// architecture (per `Every Document/STRIKZA_UI_ARCHITECTURE.md` §1
// and `docs/mode-architecture-plan.md` §B). It is consumed by the
// Phase 1 global mode-shell foundation (`src/modes/ModeShell.tsx`,
// `src/modes/ModePlaceholderPage.tsx`, `src/app/routes.tsx`).
//
// HARD CONSTRAINTS:
//   - No UI. No JSX. No React imports.
//   - No runtime side effects. Pure data + types.
//   - No icon library. No emoji.
//   - Status reflects truth — every mode is `"scaffolded"` in Step 25
//     because the global shell + stub placeholder pages render at
//     `/pitch`, `/vault`, `/brief`, `/council`, `/exchange`, but no
//     feature surfaces have migrated yet. Status flips to `"active"`
//     for a mode when its first real feature surface lands.
//
// Sidebar policy values are the *spec* (per UI-architecture §3–§7
// + §14.4 + §16) — they are not enforced as runtime checks here;
// per-mode shells will honour them when those shells are built.

export type ModeKey = "pitch" | "vault" | "brief" | "council" | "exchange";

export type ModeStatus = "planned" | "scaffolded" | "active";

// Sidebar policy per mode:
//   - "none"             → no sidebar at all (Pitch / Brief / Council)
//   - "chain-navigation" → 200px chain-navigation sidebar (Vault only)
//   - "module-section"   → 220px two-level sidebar (Exchange only)
export type ModeSidebarPolicy = "none" | "chain-navigation" | "module-section";

// Type anchor per mode (UI-architecture §14.2). Establishes the
// mode's tone; the shell is free to use other STRIKZA fonts, but the
// most prominent text uses the anchor font.
export type ModeTypeAnchor = "playfair-display" | "bebas-neue" | "jetbrains-mono" | "dm-sans";

export interface ModeConfig {
  readonly key: ModeKey;
  readonly label: string;
  readonly route: string;
  // Short tagline (one phrase). Used as a subtitle on placeholder pages.
  readonly world: string;
  // Two-letter abbreviation rendered on the desktop mode-switcher rail
  // because STRIKZA has no icon library and no emoji.
  readonly switcherAbbrev: string;
  // 1–2 sentence plain description of the mode's purpose.
  readonly description: string;
  // What feature surfaces will live in this mode once shells migrate.
  readonly futureHomeSummary: string;
  readonly sidebarPolicy: ModeSidebarPolicy;
  readonly typeAnchor: ModeTypeAnchor;
  readonly status: ModeStatus;
}

// Locked five-mode metadata. Order matches STRIKZA_UI_ARCHITECTURE.md §1.
export const STRIKZA_MODES: readonly ModeConfig[] = [
  {
    key: "pitch",
    label: "The Pitch",
    route: "/pitch",
    world: "Free tier — daily acca + Founder's Pick",
    switcherAbbrev: "PT",
    description:
      "The Pitch is STRIKZA's public, free-tier surface — a wide editorial space for the daily acca, Founder's Pick, and free-to-paid conversion narrative.",
    futureHomeSummary:
      "Daily acca card, Founder's Pick panel, free-tier conversion strip, and SIKA teaser will live here once The Pitch shell is built and unauthenticated access is enabled.",
    sidebarPolicy: "none",
    typeAnchor: "playfair-display",
    status: "scaffolded",
  },
  {
    key: "vault",
    label: "The Vault",
    route: "/vault",
    world: "ROLLOVER chains, chain progress, protected amounts",
    switcherAbbrev: "VT",
    description:
      "The Vault is the private ROLLOVER terminal — chain hero block, week-by-week progress track, active week card, and chain history.",
    futureHomeSummary:
      "ROLLOVER chain dashboard, chain navigation sidebar, post-win compounding decisions, and chain history will live here once The Vault shell + chain progression engine ship.",
    sidebarPolicy: "chain-navigation",
    typeAnchor: "bebas-neue",
    status: "active",
  },
  {
    key: "brief",
    label: "The Brief",
    route: "/brief",
    world: "Slip presentation, agent reasoning, placement guidance",
    switcherAbbrev: "BR",
    description:
      "The Brief is the slip dossier — focused single-column presentation of legs, agent reasoning, odds, and placement guidance with a sticky action bar.",
    futureHomeSummary:
      "Phantom slip presentation, real slip presentation, agent reasoning panel, and slip history (`/brief/history`) will migrate here from the temporary dashboard once The Brief shell is built.",
    sidebarPolicy: "none",
    typeAnchor: "jetbrains-mono",
    status: "active",
  },
  {
    key: "council",
    label: "The Council",
    route: "/council",
    world: "SIKA conversation, goal planning, simulator",
    switcherAbbrev: "CL",
    description:
      "The Council will become the private SIKA room for goal planning, goal simulation, and disciplined bankroll planning. Phase-1A preview only.",
    futureHomeSummary:
      "SIKA conversation, goal planner, goal simulator, and SIKA Pro home will live here once The Council shell + SIKA agent ship.",
    sidebarPolicy: "none",
    typeAnchor: "dm-sans",
    status: "scaffolded",
  },
  {
    key: "exchange",
    label: "The Exchange",
    route: "/exchange",
    world: "Forex / Crypto / Polymarket / Equity signals",
    switcherAbbrev: "EX",
    description:
      "The Exchange is the institutional trading floor — dense signal cards, two-level module/section navigation, and module-color coding (Forex / Crypto / Polymarket / Equity).",
    futureHomeSummary:
      "Expansion-module signal cards, module/section sidebar, and cross-market signal rollups will live here once The Exchange shell + module engines ship.",
    sidebarPolicy: "module-section",
    typeAnchor: "jetbrains-mono",
    status: "scaffolded",
  },
] as const;

// Lookup helper. Throws on unknown keys so callers fail loudly during
// development rather than rendering a silent empty mode shell.
export function getModeConfig(key: ModeKey): ModeConfig {
  const cfg = STRIKZA_MODES.find((m) => m.key === key);
  if (!cfg) {
    throw new Error(`Unknown mode key: ${key}`);
  }
  return cfg;
}

// Route prefixes keyed by ModeKey. Consumed by `src/app/routes.tsx`
// to wire stub mode routes inside `<AuthGate>` + `<AppRoutes>`.
export const MODE_ROUTE_PREFIXES: { readonly [K in ModeKey]: string } = {
  pitch: "/pitch",
  vault: "/vault",
  brief: "/brief",
  council: "/council",
  exchange: "/exchange",
};
